import React, { useCallback, useMemo, useState } from "react";

import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";

import { ManualAdjustmentAction } from "actions";

import AgGrid from "components/AgGrid/AgGrid";
import AgGridTable from "components/AgGrid/AgGridContainer";
import { RowSelectionMode } from "components/AgGrid/constants";
import { ModalTableWrapper } from "components/AgGrid/TableWrapper";
import {
  ConfirmDialog,
  createModalTitle,
  MESSAGE_CANT_BE_UNDONE,
} from "components/ConfirmDialog";
import { Button } from "components/Form";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";
import { columnDefs, mobileColumnDefs } from "components/Sundry/Table/columns";

import { AgGridTables } from "constants/aggrid";
import { ApiModel } from "constants/loading";
import { BusinessModalSection, ModalTypes } from "constants/navigation";

import { getSelectedManualAdjustmentIds } from "lib/agGrid/getSelectedManualAdjustmentIds";
import { openEditBusinessModal, openModalLink } from "lib/navigation";
import { pluralize } from "lib/pluralize";

import {
  getActiveBillingRunId,
  getIsReadOnlyByBillingRunId,
  selectActiveLivestockAgentDeploymentMasterBusinessId,
  selectManualAdjustmentsData,
} from "selectors";

import { useIsMobile } from "hooks/useScreenSize";

const getRowId = params => params.data.manualAdjustment.id;

export const Table = () => {
  const isMobile = useIsMobile();
  const dispatch = useDispatch();
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteIds, setDeleteIds] = useState([]);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const rowData = useSelector(selectManualAdjustmentsData);
  const activeBillingRunId = useSelector(getActiveBillingRunId);
  const isReadOnly = useSelector(
    getIsReadOnlyByBillingRunId(activeBillingRunId),
  );

  const selectedIds = useMemo(
    () => getSelectedManualAdjustmentIds(selectedRows),
    [selectedRows],
  );
  const selectedCount = selectedIds.length;

  const additionalActions = useMemo(
    () =>
      [
        {
          title: "Add",
          icon: faPlus,
          onClick: () => openModalLink(ModalTypes.ManualAdjustmentForm),
          default: true,
          dataTour: "add-manual-charge",
        },
        !isReadOnly && {
          title: selectedCount ? `Delete (${selectedCount})` : "Delete",
          icon: faTrash,
          isDisabled: selectedCount === 0,
          onClick: () => {
            setIsBulkDelete(true);
            setDeleteIds(selectedIds);
          },
          dataTour: "bulk-delete-manual-adjustments",
        },
      ].filter(Boolean),
    [isReadOnly, selectedCount, selectedIds],
  );

  const context = useMemo(
    () => ({
      canDeleteManualAdjustment: !isReadOnly,
      onDeleteManualAdjustment: id => {
        setIsBulkDelete(false);
        setDeleteIds([id]);
      },
    }),
    [isReadOnly],
  );

  const onConfirmDelete = useCallback(() => {
    dispatch(ManualAdjustmentAction.deleteBulk(deleteIds));
    setDeleteIds([]);
    if (isBulkDelete) {
      setSelectedRows([]);
    }
  }, [deleteIds, dispatch, isBulkDelete]);

  const activeLivestockAgentDeploymentMasterBusinessId = useSelector(
    selectActiveLivestockAgentDeploymentMasterBusinessId,
  );

  const getContextMenuItems = useCallback(
    params => {
      const contextMenu = Array.isArray(params.defaultItems)
        ? [...params.defaultItems]
        : [];
      contextMenu.push({
        name: "Add Relationship",
        action: () =>
          openEditBusinessModal(
            activeLivestockAgentDeploymentMasterBusinessId,
            undefined,
            BusinessModalSection.BUSINESS_RELATIONSHIPS,
          ),
      });

      return contextMenu;
    },
    [activeLivestockAgentDeploymentMasterBusinessId],
  );

  return (
    <>
      <WaitForSync requiredData={[ApiModel.BUSINESSES]}>
        <AgGridTable
          additionalActions={additionalActions}
          columnDefs={isMobile ? mobileColumnDefs : columnDefs}
          context={context}
          getRowId={getRowId}
          rowData={rowData}
          tableName={AgGridTables.MANUAL_ADJUSTMENT}
          showGlobalSearchFilter={false}
          getContextMenuItems={getContextMenuItems}
          suppressCellSelection={isMobile}
          hideSideBar={isMobile}
          hideSavedViews={isMobile}
          onRowSelectionChanged={setSelectedRows}
          rowSelection={RowSelectionMode.MULTIPLE}
          rowsSelectable={!isReadOnly}
        />
      </WaitForSync>
      <ConfirmDialog
        destructive
        isOpen={deleteIds.length > 0}
        message={MESSAGE_CANT_BE_UNDONE}
        onCancel={() => setDeleteIds([])}
        onDelete={onConfirmDelete}
        title={createModalTitle(
          `${deleteIds.length} ${pluralize("sundry", deleteIds.length)}`,
        )}
      />
    </>
  );
};

export const Modal = ({ onClose }) => {
  const isMobile = useIsMobile();
  const rowData = useSelector(selectManualAdjustmentsData);

  const onFirstDataRendered = useCallback(
    event => {
      if (isMobile) {
        event.api.autoSizeAllColumns();
      }
    },
    [isMobile],
  );

  return (
    <Dialog fullWidth fullScreen maxWidth="lg" open onClose={onClose}>
      <DialogTitle onClose={onClose}>Sundry</DialogTitle>
      <DialogContent dividers>
        <WaitForSync requiredData={[ApiModel.BUSINESSES]}>
          <ModalTableWrapper>
            <AgGrid
              columnDefs={isMobile ? mobileColumnDefs : columnDefs}
              getRowId={getRowId}
              rowData={rowData}
              tableName={AgGridTables.MANUAL_ADJUSTMENT}
              onFirstDataRendered={onFirstDataRendered}
              suppressColumnVirtualisation={isMobile}
            />
          </ModalTableWrapper>
        </WaitForSync>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
